/**
 * Tiny DOM helper. Avoids the boilerplate of createElement/setAttribute/appendChild
 * without dragging in a framework.
 */

type Child = Node | string | number | false | null | undefined;

type Attrs = Record<string, string | number | boolean | EventListener | null | undefined>;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Attrs,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [key, val] of Object.entries(attrs)) {
      if (val === null || val === undefined || val === false) continue;
      if (key === 'class' || key === 'className') {
        el.className = String(val);
      } else if (key.startsWith('on') && typeof val === 'function') {
        el.addEventListener(key.slice(2).toLowerCase(), val as EventListener);
      } else {
        el.setAttribute(key, String(val));
      }
    }
  }
  for (const child of children.flat(Infinity as 1)) {
    if (child === false || child === null || child === undefined) continue;
    el.appendChild(typeof child === 'string' || typeof child === 'number'
      ? document.createTextNode(String(child))
      : child);
  }
  return el;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

type SvgAttrs = Record<string, string | number>;
type SvgChild = SVGElement | string;

export function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs?: SvgAttrs,
  ...children: SvgChild[]
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      el.setAttribute(k, String(v));
    }
  }
  for (const c of children) {
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}
